import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Plus, Pencil, Trash2, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const UNITS = [
  { value: 'fixed', label: 'Fixed amount' },
  { value: 'per_hour', label: 'Per hour' },
  { value: 'per_day', label: 'Per day' },
];

const emptyForm = { name: '', unit: 'fixed', default_amount: 0, taxable: true, description: '' };

export default function AllowanceTypesTab({ api }) {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });

  const fetchTypes = async () => {
    try {
      const res = await api.get('/allowance-types');
      setTypes(res.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { fetchTypes(); }, []);

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    try {
      if (editing) {
        await api.put(`/allowance-types/${editing.id}`, form);
        toast.success('Allowance type updated');
      } else {
        await api.post('/allowance-types', form);
        toast.success('Allowance type created');
      }
      setDialogOpen(false);
      setEditing(null);
      setForm({ ...emptyForm });
      fetchTypes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleEdit = (t) => {
    setEditing(t);
    setForm({ name: t.name, unit: t.unit, default_amount: t.default_amount || 0, taxable: t.taxable ?? true, description: t.description || '' });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/allowance-types/${deleteTarget.id}`);
      toast.success('Allowance type deleted');
      setDeleteTarget(null);
      fetchTypes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleToggle = async (t) => {
    try {
      await api.put(`/allowance-types/${t.id}`, { is_active: !t.is_active });
      fetchTypes();
    } catch { toast.error('Failed to toggle'); }
  };

  const unitLabel = (u) => UNITS.find(x => x.value === u)?.label || u;

  if (loading) return <div className="text-muted-foreground py-8 text-center">Loading...</div>;

  return (
    <Card className="card-industrial">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="font-heading flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-[#ffd108]" /> Allowance Types
          </CardTitle>
          <CardDescription>Configure allowance categories that can be added to timesheets (e.g. Travel, Meal, Site Allowance).</CardDescription>
        </div>
        <Button
          onClick={() => { setEditing(null); setForm({ ...emptyForm }); setDialogOpen(true); }}
          className="bg-[#ffd108] text-black hover:bg-[#e5bc07]"
          data-testid="add-allowance-type-btn"
        >
          <Plus className="h-4 w-4 mr-2" /> Add Type
        </Button>
      </CardHeader>
      <CardContent>
        {types.length === 0 ? (
          <p className="text-muted-foreground text-center py-6">No allowance types configured.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead>Default Amount</TableHead>
                <TableHead>Taxable</TableHead>
                <TableHead>Active</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {types.map((t) => (
                <TableRow key={t.id} data-testid={`allowance-type-row-${t.id}`}>
                  <TableCell className="font-medium">{t.name}</TableCell>
                  <TableCell><Badge variant="outline">{unitLabel(t.unit)}</Badge></TableCell>
                  <TableCell>${(t.default_amount || 0).toFixed(2)}</TableCell>
                  <TableCell>{t.taxable ? 'Yes' : 'No'}</TableCell>
                  <TableCell>
                    <Switch checked={t.is_active} onCheckedChange={() => handleToggle(t)} />
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(t)} data-testid={`edit-allowance-${t.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="text-red-500" onClick={() => setDeleteTarget(t)} data-testid={`delete-allowance-${t.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit' : 'Add'} Allowance Type</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Travel, Meal, Site Allowance"
                data-testid="allowance-type-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Unit</Label>
                <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                  <SelectTrigger data-testid="allowance-type-unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNITS.map(u => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Default Amount ($)</Label>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={form.default_amount}
                  onChange={(e) => setForm({ ...form, default_amount: parseFloat(e.target.value) || 0 })}
                  data-testid="allowance-type-amount-input"
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={form.taxable}
                onCheckedChange={(v) => setForm({ ...form, taxable: v })}
                data-testid="allowance-type-taxable-switch"
              />
              <Label>Taxable</Label>
            </div>
            <div className="space-y-1.5">
              <Label>Description (optional)</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Brief description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} className="bg-[#ffd108] text-black hover:bg-[#e5bc07]" data-testid="save-allowance-type-btn">
              {editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Allowance Type</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteTarget?.name}"? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
